import { HTMLAttributes, useEffect, useRef, useState } from "react";
import StyledAutocomplete from "./StyledAutocomplete";
import { TextField, TextFieldProps, Tooltip, Box } from "@mui/material";
import { P } from "theme/typography";
import useDebounce from "helpers/useDebounce";
import LoadingSpinner from "./LoadingSpinner";
import { runGqlQuery } from "gqlClient";
import { type SearchCasesResult, SearchLensCasesDocument } from "generated/graphql";
import { useSnackbar } from "notistack";
import theme from "theme";

export interface CaseAutocompleteProps {
  label: string;
  lensId: number;
  assigneeId?: number;
  disabled?: boolean;
  selected?: SearchCasesResult;
  onChange: (selected: SearchCasesResult | undefined) => void;
  variant?: TextFieldProps["variant"];
  clearOnChange?: boolean;
}

const renderCase = (props: HTMLAttributes<HTMLLIElement>, c: SearchCasesResult) => {
  return (
    <li {...props} key={c.caseId}>
      <Box sx={{ width: "100%" }}>
        <P noWrap variant="subtitle2">
          {c.claimantName}
        </P>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Tooltip title="Case ID" arrow placement="left">
            <P sx={{ color: theme.palette.grey[300] }} variant="xsBold">
              {c.clientCaseId}
            </P>
          </Tooltip>
          <Tooltip title="Assignee" arrow placement="right">
            <P sx={{ color: theme.palette.grey[300] }} variant="xsBold">
              {c.assigneeFullName}
            </P>
          </Tooltip>
        </Box>
      </Box>
    </li>
  );
};

const CaseAutocomplete = (props: CaseAutocompleteProps) => {
  const { label, lensId, assigneeId, disabled, selected, onChange, variant, clearOnChange } = props;
  const [options, setOptions] = useState<SearchCasesResult[]>([]);
  const [textQuery, setTextQuery] = useState<string>("");
  const debouncedQuery = useDebounce(textQuery, 250);
  const [opened, setOpened] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const clearIndicatorRef = useRef<HTMLButtonElement>(null);
  const { enqueueSnackbar } = useSnackbar();

  const validQuery = debouncedQuery.length > 0;
  useEffect(() => {
    let canceled = false;
    if (opened && validQuery) {
      setLoading(true);
      runGqlQuery(SearchLensCasesDocument, { query: textQuery, lensId, assigneeId: assigneeId ?? null, limit: 20 })
        .then((res) => {
          if (!canceled) {
            setOptions(res.lens.searchCases);
            setLoading(false);
          }
        })
        .catch(() => {
          enqueueSnackbar("There was a problem searching cases.", { variant: "error" });
          setLoading(false);
        });
    }
    return () => {
      canceled = true;
    };
  }, [lensId, assigneeId, debouncedQuery, opened]);

  return (
    <StyledAutocomplete<SearchCasesResult, false, false, false>
      freeSolo={false}
      multiple={false}
      disabled={disabled}
      autoHighlight
      autoComplete
      componentsProps={{
        clearIndicator: {
          ref: clearIndicatorRef,
        },
      }}
      size="small"
      fullWidth
      options={options}
      noOptionsText={loading ? <LoadingSpinner size={40} /> : validQuery ? "No matches" : "Search by case id or name"}
      getOptionDisabled={(_item) => loading}
      onOpen={() => setOpened(true)}
      value={selected}
      // Override filterOptions to never filter out; the default uses the query text
      filterOptions={(all) => all}
      onChange={(_evt, item) => onChange(item ?? undefined)}
      onClose={() => {
        if (clearOnChange) {
          setTimeout(() => {
            const ele = clearIndicatorRef ? clearIndicatorRef.current : null;
            if (ele) ele.click();
          }, 200);
        }
      }}
      inputValue={textQuery}
      onInputChange={(_evt, newInputValue) => {
        setTextQuery(newInputValue || "");
      }}
      renderOption={(htmlAttrs, value) => renderCase(htmlAttrs, value)}
      isOptionEqualToValue={(option, value) => option.caseId === value.caseId}
      getOptionLabel={(c: SearchCasesResult) => c.claimantName}
      renderInput={(params) => (
        <TextField {...params} color="secondary" label={label} variant={variant || "outlined"} fullWidth />
      )}
    />
  );
};

export default CaseAutocomplete;
